import type { MfgPurchaseOrder } from "./purchaseOrderTypes.js";

const INVOICE_ELIGIBLE = new Set(["issued", "confirmed", "partially_received", "received"]);

/** Invoice amount wins over the predicted PO total. */
export function effectivePoTotalUsd(
  order: Pick<MfgPurchaseOrder, "total" | "subtotal" | "invoice_total_usd">
): number {
  const inv = order.invoice_total_usd;
  if (inv != null && Number.isFinite(inv)) return Math.round(inv * 100) / 100;
  const predicted = order.total ?? order.subtotal;
  if (predicted != null && Number.isFinite(predicted)) return Math.round(predicted * 100) / 100;
  return 0;
}

export function poHasInvoiceRecord(
  order: Pick<MfgPurchaseOrder, "invoice_recorded_at" | "invoice_total_usd">
): boolean {
  if (order.invoice_recorded_at) return true;
  return order.invoice_total_usd != null && Number.isFinite(order.invoice_total_usd);
}

export function poNeedsInvoiceRecord(
  order: Pick<MfgPurchaseOrder, "status" | "invoice_recorded_at" | "invoice_total_usd">
): boolean {
  return INVOICE_ELIGIBLE.has(order.status) && !poHasInvoiceRecord(order);
}

export function parseInvoicePayTerms(raw: unknown): { ok: true; value: string } | { ok: false; error: string } {
  const value = typeof raw === "string" ? raw.trim() : "";
  if (!value) return { ok: false, error: "Pay terms are required (for example Net 30 or Due on receipt)." };
  if (value.length > 80) return { ok: false, error: "Pay terms must be 80 characters or fewer." };
  return { ok: true, value };
}

export function parseInvoiceTotalUsd(raw: unknown): { ok: true; value: number } | { ok: false; error: string } {
  const n =
    typeof raw === "number"
      ? raw
      : typeof raw === "string" && raw.trim()
        ? Number(raw.replace(/[$,]/g, ""))
        : NaN;
  if (!Number.isFinite(n) || n < 0) return { ok: false, error: "Invoice amount must be a non-negative number." };
  return { ok: true, value: Math.round(n * 100) / 100 };
}

export function parseOptionalIsoDate(
  raw: unknown,
  label: string
): { ok: true; value: string | null } | { ok: false; error: string } {
  if (raw == null || raw === "") return { ok: true, value: null };
  if (typeof raw !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) {
    return { ok: false, error: `${label} must be YYYY-MM-DD.` };
  }
  return { ok: true, value: raw.trim() };
}

